import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChildren, ChangeDetectorRef,
  Output, EventEmitter, QueryList, HostListener, AfterViewInit
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { trigger, transition, style, state, animate } from '@angular/animations';
import { Store } from '@ngrx/store';
import { Router, ActivatedRouteSnapshot } from '@angular/router';

import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';

import { Observable, ReplaySubject, throwError } from 'rxjs';
import { takeUntil, distinctUntilChanged, debounceTime, switchMap, catchError, filter, map } from 'rxjs/operators';

import { Selectors, AppState, Actions } from 'store/root-reducer';
import { State as AuthState } from 'store/reducers/auth.reducers';
import { Domain } from 'modules/shared/models/domain.models';

import { DropdownMenuComponent } from 'modules/shared/components/dropdown-menu/dropdown-menu.component';
import { SelectDomainPopupComponent } from 'modules/core/components/select-domain-popup/select-domain-popup.component';

import { Customer } from 'modules/shared/models/customer.models';
import {
  FilterItem, Filter, CustomerFilter, SearchCustomerStatusOptions, SearchCustomerRiskRatingOptions,
  SearchCustomerRecordTypeOptions, SearchCustomerIsActiveOptions
} from 'modules/shared/models/filter.models';
import { CustomerResponse } from 'store/models/customer.models';
import { Role } from 'modules/shared/models/role.models';

import { CustomerService, ICustomersRequest } from 'modules/core/services/customer.service';
import { SnackbarService } from 'app/services/snackbar.service';

import { environment } from 'environments/environment';

import { TranslateService } from '@ngx-translate/core';

interface IFoundCustomer {
  id: number;
  name: string;
}

@Component({
  selector: 'artemis-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      state('true', style({
        opacity: 1,
      })),
      state('false', style({
        opacity: 0,
      })),
      transition('0 => 1', animate('300ms ease-in-out'))
    ]),
  ]
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() toggleSidebar: EventEmitter<boolean> = new EventEmitter();

  @ViewChildren('customersDropdown') customersDropdowns: QueryList<DropdownMenuComponent>;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  filterSettings: Filter[] = [];
  searchAllParam: CustomerFilter = {
    recordType: '',
    risks: '',
    active: '',
    status: '',
    integrationStatus: ''
  };

  searchFormControl: FormControl;

  customers: Array<IFoundCustomer>;

  isSearchBarOpened: boolean = false;
  matSelect: Array<FilterItem>;

  roles$: Observable<string>;
  profileStore$: Observable<AuthState> = this.store.select(Selectors.auth.getAuth);
  currentDomainStore$: Observable<Domain> = this.store.select(Selectors.domain.getCurrentDomain);

  isCurrentThemeLight: boolean = false;

  enableAresIntegration: boolean = environment['ENABLE_ARES_INTEGRATION'];

  @HostListener('document:click', ['$event']) onClick($event: any) {
    const clickedSearchComponent: boolean = this.customersDropdowns.some((customerDropdown: DropdownMenuComponent) => {
      return customerDropdown.elRef.nativeElement.parentElement.contains($event.target);
    });
    if (!clickedSearchComponent) {
      this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(false));
    }
  }

  constructor(
    protected translateService: TranslateService,
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private router: Router,
    private snackBar: SnackbarService,
    private dialog: MatDialog,
    private customersService: CustomerService) {
    this.filterSettings = <Filter[]>[
      {
        name: 'status',
        title: `${translateService.instant('general.status.title')}`,
        items: [
          {
            subType: 'status',
            key: `${translateService.instant('general.status.draft')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.draft')}`]
          }, {
            subType: 'status',
            key: `${translateService.instant('general.status.pending')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.pending')}`]
          }, {
            subType: 'status',
            key: `${translateService.instant('general.status.accepted')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.accepted')}`]
          }, {
            subType: 'status',
            key: `${translateService.instant('general.status.cleared')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.cleared')}`]
          }, {
            subType: 'status',
            key: `${translateService.instant('general.status.rejected')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.rejected')}`]
          }, {
            subType: 'status',
            key: `${translateService.instant('general.status.request_changes')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.request_changes')}`]
          }
        ]
      },
      {
        name: 'risk',
        title: `${translateService.instant('general.risk_rating.title')}`,
        items: [
          {
            subType: 'risk',
            key: `${translateService.instant('general.risk_rating.low')}`,
            label: SearchCustomerRiskRatingOptions[`${translateService.instant('general.risk_rating.low')}`]
          }, {
            subType: 'risk',
            key: `${translateService.instant('general.risk_rating.medium')}`,
            label: SearchCustomerRiskRatingOptions[`${translateService.instant('general.risk_rating.medium')}`]

          }, {
            subType: 'risk',
            key: `${translateService.instant('general.risk_rating.high')}`,
            label: SearchCustomerRiskRatingOptions[`${translateService.instant('general.risk_rating.high')}`]
          }, {
            subType: 'risk',
            key: `${translateService.instant('general.risk_rating.unknown')}`,
            label: SearchCustomerRiskRatingOptions[`${translateService.instant('general.risk_rating.unknown')}`]
          }
        ]
      },
      {
        name: 'record_type',
        title: `${translateService.instant('general.customer_type.title')}`,
        items: [
          {
            subType: 'record_type',
            key: `${translateService.instant('general.customer_type.individual')}`,
            label: SearchCustomerRecordTypeOptions[`${translateService.instant('general.customer_type.individual')}`]

          }, {
            subType: 'record_type',
            key: `${translateService.instant('general.customer_type.corporate')}`,
            label: SearchCustomerRecordTypeOptions[`${translateService.instant('general.customer_type.corporate')}`]

          }
        ]
      },
      {
        name: 'is_active_customer',
        title: `${translateService.instant('general.active.title')}`,
        items: [
          {
            subType: 'is_active_customer',
            key: `${translateService.instant('general.active.title')}`,
            label: SearchCustomerIsActiveOptions[`${translateService.instant('general.active.title')}`]
          }, {
            subType: 'is_active_customer',
            key: `${translateService.instant('general.active.inactive')}`,
            label: SearchCustomerIsActiveOptions[`${translateService.instant('general.active.inactive')}`]
          }
        ]
      }];

    if (environment['ENABLE_ARES_INTEGRATION']) {
      const customerTypeFilterIndex: number = this.filterSettings.findIndex((filterCategory: Filter) => filterCategory.name === 'status');
      const newCustomerTypesFilterList: Array<FilterItem> = this.filterSettings[customerTypeFilterIndex].items
        .concat([
          {
            subType: 'integrationStatus',
            key: `${translateService.instant('general.status.require_update')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.require_update')}`]
          }, {
            subType: 'integrationStatus',
            key: `${translateService.instant('general.status.updated')}`,
            label: SearchCustomerStatusOptions[`${translateService.instant('general.status.updated')}`]
          }
        ]);

      this.filterSettings[customerTypeFilterIndex].items = newCustomerTypesFilterList;
    }
  }

  ngOnInit() {
    this.searchFormControl = new FormControl('');
    this.reloadDashboardPage();

    this.profileStore$ = this.store.select(Selectors.auth.getAuth);
    this.currentDomainStore$ = this.store.select(Selectors.domain.getCurrentDomain);
    this.roles$ = this.store
      .select(Selectors.auth.getUserRoles)
      .pipe(map((res: Array<Role>) => res.map((role: any) => role.name).sort().join(', ')));
    this.initSearchSubscription();
  }

  ngAfterViewInit() {
    this.checkCurrentTheme();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private checkCurrentTheme(): void {
    const currentTheme: string = document.body.className.split(' ').find((value: string) => !!~value.search(/theme_(\w)+/g));

    switch (currentTheme) {
      case 'theme_dark':
        this.isCurrentThemeLight = false;
        break;

      case 'theme_light':
        this.isCurrentThemeLight = true;
        break;
    }

    this.cdRef.detectChanges();
  }

  checkRolesToHidden(roles: Array<string>): boolean {
    let listRole: Array<string> = [];
    let isHide: boolean = false;
    this.roles$.subscribe(value => {
        listRole = value.split(', ');
        if (listRole.length <= 2) {
          for (const e of listRole) {
            if (!roles.includes(e)) {
              isHide = false;
              break;
            } else {
              isHide = true;
            }
          }
        }
    });
    return isHide;
  }

  /**
   * Reloads dashboard page after selecting domain if dashboard is current page
   * @method reloadDashboardPage
   */
  private reloadDashboardPage(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = function (futureRoute: ActivatedRouteSnapshot, currRoute: ActivatedRouteSnapshot) {
      if (futureRoute.url.toString() === 'dashboard' && currRoute.url.toString() === futureRoute.url.toString()) {
        return false;
      }
      return (futureRoute.routeConfig === currRoute.routeConfig);
    };
  }

  /**
   * Init search customers subscription
   * @method initSearchSubscription
   */
  private initSearchSubscription(): void {
    this.searchFormControl
      .valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(150),
        filter((newSearchValue: string) => {
          this.searchFormControl.setValue(newSearchValue, { emitEvent: false });
          if (!newSearchValue) {
            this.customers = null;
            this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(false));
            this.cdRef.detectChanges();
          }
          return !!newSearchValue;
        }),
        switchMap((newSearchValue: string) => {
          const requestObject: ICustomersRequest = <ICustomersRequest>{
            name: newSearchValue,
            limit: 10
          };
          return this.customersService.getCustomersExtended(requestObject);
        }),
        catchError((error: any) => throwError(error)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (foundCustomers: CustomerResponse) => {
          // Switch map emits after filter didn't emit
          if (this.searchFormControl.value) {
            this.customers = foundCustomers.results.map((customer: Customer) => {
              return <IFoundCustomer>{
                name: customer.name,
                id: customer.id
              };
            });
            this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(true));
            this.cdRef.detectChanges();
          }
        },
        (error: any) => {
          if (error.error) {
            this.snackBar.error(error.error.detail);
          } else if (typeof error === 'string') {
            this.snackBar.error(error);
          } else {
            this.snackBar.error(error.message);
          }
        }
      );
  }

  /**
   * Handler when filter fires
   * @method onSelectionChangeHandler
   */
  onSelectionChangeHandler(filterData: MatSelectChange): void {
    this.searchAllParam.risks = filterData.value
      .filter((filterItem: FilterItem) => filterItem.subType === 'risk')
      .map((filterItem: FilterItem) => filterItem.key)
      .join(',');

    this.searchAllParam.active = filterData.value
      .filter((filterItem: FilterItem) => filterItem.subType === 'is_active_customer')
      .map((filterItem: FilterItem) => filterItem.key)
      .join(',');

    this.searchAllParam.recordType = filterData.value
      .filter((filterItem: FilterItem) => filterItem.subType === 'record_type')
      .map((filterItem: FilterItem) => filterItem.key)
      .join(',');

    this.searchAllParam.status = filterData.value
      .filter((filterItem: FilterItem) => filterItem.subType === 'status')
      .map((filterItem: FilterItem) => filterItem.key)
      .join(',');

    this.searchAllParam.integrationStatus = filterData.value
      .filter((filterItem: FilterItem) => filterItem.subType === 'integrationStatus')
      .map((filterItem: FilterItem) => filterItem.key)
      .join(',');
  }

  /**
   * Clear customers search
   * @method resetCustomersSearch
   */
  resetCustomersSearch(): void {
    this.customers = null;
    this.searchFormControl.reset();
    this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(true));
    this.toggleMobileSearchBar(false);
    this.cdRef.detectChanges();
  }

  /**
   * Logout user and takes him to login page
   * @method logOut
   */
  logOut(): void {
    this.store.dispatch(new Actions.auth.LogOut({ isSessionTimeOut: false }));
  }

  /**
   * Click on burger icon to open sidebar
   * @method toggleSidebarClick
   */
  toggleSidebarClick(): void {
    this.toggleSidebar.next(true);
  }

  /**
   * Toggles mobile search bar
   * @method toggleMobileSearchBar
   */
  toggleMobileSearchBar(open?: boolean): void {
    if (open !== undefined) {
      this.isSearchBarOpened = open;
      return;
    }

    this.isSearchBarOpened = !this.isSearchBarOpened;
  }

  /**
   * Select domain with modal
   * @method selectDomain
   */
  selectDomain(): void {
    const dialogRef: MatDialogRef<SelectDomainPopupComponent> = this.dialog.open(SelectDomainPopupComponent, {
      autoFocus: false
    });
    dialogRef
      .beforeClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (domain: Domain) => {
          if (domain) {
            this.store.dispatch(new Actions.domain.ChangeDomain(domain));
          }
        }
      );
  }

  /**
   * Goes to customer list with name param in url
   * @method goCustomer
   */
  goCustomer(): void {
    if (this.searchFormControl.value || this.searchAllParam) {
      const navigationParamsObject: any = {};

      if (this.searchFormControl.value) {
        navigationParamsObject.name = encodeURIComponent(this.searchFormControl.value);
      }

      if (this.searchAllParam) {
        const itemLengths = {
          status: this.filterSettings.filter(_filter => _filter.name === 'status')[0].items.length,
          risks: this.filterSettings.filter(_filter => _filter.name === 'risk')[0].items.length,
          recordType: this.filterSettings.filter(_filter => _filter.name === 'record_type')[0].items.length,
          isActiveCustomer: this.filterSettings.filter(_filter => _filter.name === 'is_active_customer')[0].items.length
        };

        if (this.searchAllParam.risks !== '' && this.searchAllParam.risks.split(',').length < itemLengths.risks) {
          navigationParamsObject.risks = this.searchAllParam.risks;
        }
        if (this.searchAllParam.recordType !== '' && this.searchAllParam.recordType.split(',').length < itemLengths.recordType) {
          navigationParamsObject.recordType = this.searchAllParam.recordType;
        }
        if (this.searchAllParam.active !== '' && this.searchAllParam.active.split(',').length < itemLengths.isActiveCustomer) {
          navigationParamsObject.active = this.searchAllParam.active;
        }
        if (this.searchAllParam.status !== '' && this.searchAllParam.status.split(',').length < itemLengths.status) {
          navigationParamsObject.status = this.searchAllParam.status;
        }
        if (this.searchAllParam.integrationStatus !== '') {
          navigationParamsObject.integrationStatus = this.searchAllParam.integrationStatus;
        }
      }

      this.router.navigate(['/customers', navigationParamsObject]);
      this.searchFormControl.reset();
      this.searchAllParam = {
        recordType: '',
        risks: '',
        active: '',
        status: '',
        integrationStatus: ''
      };
      this.matSelect = undefined;
      this.cdRef.detectChanges();
    }
  }

  /**
   * Fires when user selects customer in list, clears form and toggles dropdown
   * @method selectCustomer
   */
  selectCustomer(customer: IFoundCustomer): void {
    this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(true));
    this.searchFormControl.reset();
  }

  /**
   * Handler for focus event on search inputs
   * @method focusSearchInputsHandler
   */
  focusSearchInputsHandler($event: any): void {
    if (this.customers && this.searchFormControl.value) {
      this.customersDropdowns.forEach((customerDropdown: DropdownMenuComponent) => customerDropdown.toggleDropdown(true));
    }
  }

  /**
   * Handler for theme toggle value change
   * @method themeChanged
   */
  themeChanged(): void {
    this.isCurrentThemeLight = !this.isCurrentThemeLight;
  }
}
